import * as Y from "yjs";
import { WebrtcProvider } from "y-webrtc";
import type { YDocContent } from "./Context";
import { clearDocument, IndexeddbPersistence } from "y-indexeddb";
import { get, writable, type Writable } from "svelte/store";
import { browser } from "$app/environment";
import {
  addVersion,
  getVersions,
  getVersionsList,
  hasChanges,
  removeVersion,
  revertUpdate,
} from "./extensions/Collaboration/versions";
import { alerts } from "app/stores/alert";
import type { User } from "app/stores/user";
import { generateYDoc } from "./io";
import type { JSONContent } from "@tiptap/core";
import { PUBLIC_EDITOR_WEBRTC_KEY } from "$env/static/public";

export interface UseYDocProps {
  documentName: string;
  user: User;
  initialDocument?: Uint8Array;
  json?: JSONContent;
  useLocalDB?: boolean;
  useWebRTC?: boolean;
  clearOnLoad?: boolean;
  permanentUserData?: Y.PermanentUserData;
}

export const YDoc = ({
  documentName,
  initialDocument,
  json,
  user,
  useWebRTC = false,
  useLocalDB = true,
  clearOnLoad = false,
}: UseYDocProps) => {
  const s: Writable<YDocContent> = writable({} as YDocContent, (set) => {
    const doc = new Y.Doc({ gc: false });
    if (initialDocument?.length) {
      Y.applyUpdate(doc, initialDocument);
    } else {
      // imports + templates have json without a crdt
      if (json && Object.keys(json).length) {
        console.log("ydoc: found empty crdt but found json, generating ydoc from json");
        Y.applyUpdate(doc, Y.encodeStateAsUpdate(generateYDoc(json)));
      }
    }

    const permanentUserData = new Y.PermanentUserData(doc);

    if (browser) {
      permanentUserData.setUserMapping(doc, doc.clientID, user.handle!);
    }
    // init with previously saved version, before sync

    if (clearOnLoad) {
      // clear DB before loading it
      clearDocument(documentName);
    }

    const providers = {} as any;
    if (browser && useLocalDB) {
      const localDB = new IndexeddbPersistence(documentName, doc);
      providers.localDB = localDB;
    }
    // register with browser db provider for local + offline saving
    if (browser && useWebRTC) {
      if (PUBLIC_EDITOR_WEBRTC_KEY) {
        // register with RTC provider to sync with others
        providers.webRTC = new WebrtcProvider(documentName, doc, {
          password: PUBLIC_EDITOR_WEBRTC_KEY,
          signaling: ["wss://outverse-webrtc-a5d85079b5e3.herokuapp.com/"],
        } as any);
      } else {
        console.error("Missing PUBLIC_EDITOR_WEBRTC_KEY");
      }
    }
    set({ name: documentName, doc, loaded: true, ...providers, permanentUserData });

    return () => {
      doc.destroy();
      providers.webRTC?.destroy();
      providers.localDB?.destroy();
    };
  });

  return {
    ...s,
    clearLocalStorage: () => get(s).localDB?.clearData(),
    clear: () => clearDocument(documentName),
    serialise: () => {
      const state = get(s);
      return state.doc ? Y.encodeStateAsUpdate(state.doc) : new Uint8Array();
    },
    disconnect: () => get(s).webRTC?.disconnect(),
    connect: () => get(s).webRTC?.connect(),
    createVersion: (
      comment: string,
      author: { handle: string; profileImage: string; uuid: string }
    ) => {
      const state = get(s);
      return addVersion(state.doc, getVersions(state.doc), comment, author);
    },
    removeVersion: () => {
      const state = get(s);
      removeVersion(getVersions(state.doc));
    },
    getVersions: () => {
      const state = get(s);
      return getVersionsList(state.doc);
    },
    hasChanges: () => {
      const state = get(s);
      return hasChanges(state.doc, getVersions(state.doc));
    },
    revertToVersion: (versionID: number) => {
      const state = get(s);
      const versions = getVersionsList(state.doc);
      const latest = versions[0];
      const version = versions[versionID];
      if (latest.revertedID && latest.revertedID === version.versionID) {
        alerts.error("Cannot revert to the same version");
        return;
      }
      revertUpdate(state.doc, version, user);
    },
  };
};

export type YdocStore = ReturnType<typeof YDoc>;
